import axios from 'axios';

const API_BASE_URL = 'https://chat-llm.ciena.puffin.io/api';
const env = typeof import.meta !== 'undefined' ? import.meta.env : process.env;
const BASIC_AUTH_USERNAME = process.env?.VUE_APP_CHAT_API_USERNAME || 'ciena';
const BASIC_AUTH_PASSWORD = process.env?.VUE_APP_CHAT_API_PASSWORD || 'password!';

const buildBasicAuthHeader = () => {
  if (!BASIC_AUTH_USERNAME || !BASIC_AUTH_PASSWORD) return null;
  const credentials = btoa(`${BASIC_AUTH_USERNAME}:${BASIC_AUTH_PASSWORD}`);
  return `Basic ${credentials}`;
};

const basicAuthHeader = buildBasicAuthHeader();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes — enumeration queries with reasoning models can take 2-3 min
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(basicAuthHeader ? { Authorization: basicAuthHeader } : {}),
  },
});

// Request interceptor for logging/debugging
api.interceptors.request.use(
  (config) => {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    console.error('[API Error]', message);
    return Promise.reject(error);
  }
);

/**
 * Chat API functions
 */
export const chatApi = {
  /**
   * Send a message to the chat API (non-streamed)
   */
  async sendMessage(question, sessionId = null, signal = null) {
    const payload = { question };
    if (sessionId) {
      payload.session_id = sessionId;
    }
    const config = signal ? { signal } : {};
    const response = await api.post('/chat', payload, config);
    return response.data;
  },

  /**
   * Send a message with SSE streaming.
   * @param {string} question
   * @param {string|null} sessionId
   * @param {(delta: string) => void} onChunk - called for each content chunk
   * @param {AbortSignal|null} signal
   * @returns {Promise<object>} final response with session_id, metadata, etc.
   */
  async sendMessageStreamed(question, sessionId = null, onChunk = null, signal = null) {
    const payload = { question, stream: true };
    if (sessionId) {
      payload.session_id = sessionId;
    }

    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(basicAuthHeader ? { Authorization: basicAuthHeader } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(errorBody || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResponse = null;
    let eventType = null; // persists across reads so split chunks don't lose the event type

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE lines from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ') && eventType) {
          try {
            const data = JSON.parse(line.slice(6));

            if (eventType === 'chunk' && data.content && onChunk) {
              onChunk(data.content);
            } else if (eventType === 'done') {
              finalResponse = data;
            } else if (eventType === 'error') {
              throw new Error(data.message || 'Streaming error');
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
            console.warn('[SSE] Failed to parse data line:', line);
          }
          eventType = null; // reset after consuming a data line
        }
      }
    }

    return finalResponse;
  },

  async getSessions() {
    const response = await api.get('/chat/sessions');
    return response.data;
  },

  async deleteSession(sessionId) {
    await api.delete(`/chat/sessions/${sessionId}`);
  },
};

/**
 * Health and stats API functions
 */
export const systemApi = {
  /**
   * Check API health status
   * @returns {Promise<{status: string, database: string, pgvector: string}>}
   */
  async healthCheck() {
    const response = await api.get('/health');
    return response.data;
  },

  /**
   * Get system statistics
   * @returns {Promise<{companies: number, categories: number, questions: number, answers: number, chat_messages: number}>}
   */
  async getStats() {
    const response = await api.get('/stats');
    return response.data;
  },
};

/**
 * Companies API functions
 */
export const companiesApi = {
  /**
   * Get all companies
   * @returns {Promise<Array>}
   */
  async getAll() {
    const response = await api.get('/companies');
    return response.data;
  },

  /**
   * Search companies by query
   * @param {string} query - Search query
   * @param {number} limit - Max results
   * @returns {Promise<Array>}
   */
  async search(query, limit = 10) {
    const response = await api.get('/companies/search', {
      params: { q: query, limit },
    });
    return response.data;
  },
};

/**
 * Answers API functions
 */
export const answersApi = {
  /**
   * Search answers by query
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Array>}
   */
  async search(query, options = {}) {
    const response = await api.get('/answers/search', {
      params: {
        q: query,
        limit: options.limit,
        threshold: options.threshold,
        company_id: options.companyId,
      },
    });
    return response.data;
  },
};

export default api;
